/**
 * Risposte rapide con "/" e inserimento di testo al cursore. Funzioni pure
 * (test in __tests__/quickReplies.test.ts).
 *
 * - "/" all'inizio del campo o dopo uno spazio/a capo apre la lista dei modelli
 *   (whatsapp_message_templates); cio' che segue filtra per nome o testo.
 * - Il modello scelto sostituisce solo "/parola", non il resto del testo scritto.
 * - {{nome}} e {{azienda}} si compilano dal Lead/Cliente abbinato alla chat.
 */

/** Testo cercato dopo "/" (anche vuoto) se il cursore e' su un comando, altrimenti null. */
export function detectSlashQuery(text: string, cursor: number): { query: string; start: number } | null {
  const before = text.slice(0, cursor);
  const m = before.match(/(^|\s)\/([^\s/]{0,40})$/);
  if (!m) return null;
  return { query: m[2], start: before.length - m[2].length - 1 };
}

/** Sostituisce "/query" (da start al cursore) con il testo dato. */
export function replaceSlashToken(text: string, start: number, cursor: number, insert: string): { text: string; cursor: number } {
  const next = text.slice(0, start) + insert + text.slice(cursor);
  return { text: next, cursor: start + insert.length };
}

/**
 * Inserisce al cursore (o al posto della selezione). Con `addSpace` (modelli) mette uno
 * spazio se il testo prima non ne ha; per le emoji si passa false.
 */
export function insertAtCursor(text: string, selStart: number, selEnd: number, insert: string, addSpace = true): { text: string; cursor: number } {
  const start = Math.max(0, Math.min(selStart, text.length));
  const end = Math.max(start, Math.min(selEnd, text.length));
  const before = text.slice(0, start);
  const after = text.slice(end);
  const needsSpace = addSpace && before.length > 0 && !/\s$/.test(before) && !/^\s/.test(insert);
  const piece = (needsSpace ? ' ' : '') + insert;
  return { text: before + piece + after, cursor: before.length + piece.length };
}

export interface TemplateLike { id: string; name: string; content: string; category?: string | null }

export function filterTemplates<T extends TemplateLike>(templates: T[], query: string, limit = 8): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return templates.slice(0, limit);
  const byName = templates.filter((t) => t.name.toLowerCase().includes(q));
  const byText = templates.filter((t) => !byName.includes(t) && t.content.toLowerCase().includes(q));
  return [...byName, ...byText].slice(0, limit);
}

export interface ClientVars { nome?: string; azienda?: string }

/**
 * Valori delle variabili dal Lead/Cliente. Come nei flussi automatici
 * (entityEnrichment del core): contact_name -> primo nome; senza referente, il nome
 * della ragione sociale solo se e' una persona fisica. {{azienda}} = ragione sociale.
 */
export function clientTemplateVars(client: {
  business_name?: string | null; contact_name?: string | null; entity_type?: string | null;
} | null): ClientVars {
  if (!client) return {};
  const contact = (client.contact_name || '').trim();
  const business = (client.business_name || '').trim();
  const firstToken = (s: string) => s.split(/\s+/)[0] || undefined;
  const nome = contact
    ? firstToken(contact)
    : client.entity_type === 'individual' ? firstToken(business) : undefined;
  const azienda = client.entity_type === 'individual' ? undefined : business || undefined;
  return { nome, azienda };
}

/** Sostituisce {{variabile}} con i valori noti (maiuscole ignorate); le altre restano. */
export function fillTemplateVariables(content: string, values: Record<string, string | undefined>): string {
  const lower = new Map(Object.entries(values).map(([k, v]) => [k.toLowerCase(), v]));
  return content.replace(/\{\{\s*(\w+)\s*\}\}/g, (all, key: string) => {
    const v = lower.get(key.toLowerCase());
    return v ? v : all;
  });
}

/** Variabili ancora da compilare a mano. */
export function unresolvedVariables(content: string): string[] {
  return Array.from(new Set((content.match(/\{\{\s*(\w+)\s*\}\}/g) || []).map((m) => m.replace(/[{}\s]/g, ''))));
}
